import React, { useEffect, useState } from 'react'
import Modal, {
  ModalFooter,
  ModalHeader,
  ModalScrollContent,
} from '@app/components/Modal'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import theme from '@app/ui/theme'
import FeatherIcon from '@ovaeasy/react-native-vector-icons/Feather'
import * as S from './HoleRangeModal.styles'
import {
  ALL_HOLES,
  formatHoleRange,
  holeCount,
  HOLE_RANGE_PRESETS,
  HoleRange,
  HoleRangePreset,
  holeRangePreset,
} from '../holeRange'

type Props = {
  isOpen: boolean
  startHole: number
  endHole: number
  onClose: () => void
  onSelect: (range: HoleRange) => void
}

const HoleRangeModal: React.FC<Props> = ({
  isOpen,
  startHole,
  endHole,
  onClose,
  onSelect,
}) => {
  const [range, setRange] = useState<HoleRange>({ startHole, endHole })
  const [preset, setPreset] = useState<HoleRangePreset>(
    holeRangePreset({ startHole, endHole })
  )
  // Set once the first hole of a custom range is down and the last one is still
  // to come. Holding it here is what lets the grid preview the range.
  const [anchor, setAnchor] = useState<number | null>(null)
  const [hovered, setHovered] = useState<number | null>(null)

  // Opening the modal starts from what the form holds, so cancelling really
  // does leave the range alone.
  useEffect(() => {
    if (!isOpen) return
    setRange({ startHole, endHole })
    setPreset(holeRangePreset({ startHole, endHole }))
    setAnchor(null)
    setHovered(null)
  }, [isOpen, startHole, endHole])

  const selectPreset = (value: HoleRangePreset) => {
    setPreset(value)
    setAnchor(null)
    // Custom keeps the range it inherited, so the grid opens on something to
    // adjust rather than on nothing at all.
    const presetRange = HOLE_RANGE_PRESETS.find((p) => p.value === value)?.range
    if (presetRange) setRange(presetRange)
  }

  const pickHole = (hole: number) => {
    setPreset('custom')
    if (anchor === null) {
      setAnchor(hole)
      setRange({ startHole: hole, endHole: hole })
      return
    }
    setRange({
      startHole: Math.min(anchor, hole),
      endHole: Math.max(anchor, hole),
    })
    setAnchor(null)
  }

  // While the last hole is still open the grid shows where the range would
  // land, so the choice is visible before it is made.
  const shown =
    anchor !== null && hovered !== null
      ? {
          startHole: Math.min(anchor, hovered),
          endHole: Math.max(anchor, hovered),
        }
      : range

  const count = holeCount(shown)

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="600px" height="auto">
      <ModalHeader>
        <S.Title>Select holes</S.Title>
        <S.Subtitle>Which holes this slope table covers.</S.Subtitle>
      </ModalHeader>
      <ModalScrollContent>
        <S.Body>
          <S.PresetGrid>
            {HOLE_RANGE_PRESETS.map(({ value, label, hint }) => (
              <S.PresetCard
                key={value}
                type="button"
                isSelected={preset === value}
                onClick={() => selectPreset(value)}
              >
                <S.PresetText>
                  <S.PresetLabel>{label}</S.PresetLabel>
                  <S.PresetHint>{hint}</S.PresetHint>
                </S.PresetText>
                <S.PresetCheck isSelected={preset === value}>
                  {preset === value && (
                    <FeatherIcon
                      name="check"
                      size={12}
                      color={theme.colors.white}
                    />
                  )}
                </S.PresetCheck>
              </S.PresetCard>
            ))}
          </S.PresetGrid>
          {preset === 'custom' && (
            <>
              <S.CustomHint>
                {anchor === null
                  ? 'Tap the first hole, then the last one.'
                  : 'Now tap the last hole.'}
              </S.CustomHint>
              <S.HoleGrid onMouseLeave={() => setHovered(null)}>
                {ALL_HOLES.map((hole) => (
                  <S.HoleChip
                    key={hole}
                    type="button"
                    isEdge={hole === shown.startHole || hole === shown.endHole}
                    inRange={hole > shown.startHole && hole < shown.endHole}
                    onMouseEnter={() => setHovered(hole)}
                    onClick={() => pickHole(hole)}
                  >
                    {hole}
                  </S.HoleChip>
                ))}
              </S.HoleGrid>
            </>
          )}
          <S.Summary>
            <S.SummaryRange>
              {formatHoleRange(shown.startHole, shown.endHole)}
            </S.SummaryRange>
            <S.SummaryCount>
              {count === 1 ? '1 hole' : `${count} holes`}
            </S.SummaryCount>
          </S.Summary>
        </S.Body>
      </ModalScrollContent>
      <ModalFooter>
        <RoundedButton
          title="Cancel"
          onPress={onClose}
          style={{
            backgroundColor: theme.colors.white,
            border: `1px solid ${theme.colors.secondary}`,
            color: theme.colors.secondary,
          }}
          textStyle={{ color: theme.colors.secondary }}
        />
        <RoundedButton
          title="Select"
          onPress={() => {
            onSelect(range)
            onClose()
          }}
          style={{ backgroundColor: theme.colors.secondary2 }}
        />
      </ModalFooter>
    </Modal>
  )
}

export default HoleRangeModal
